import { getToolSlugByPath, getCanonicalSlugForToolType } from '../data/toolSlugs';
import { getSEOData } from '../data/seoData';

export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-PTX9TKG6V3';

export const CONSENT_STORAGE_KEY = 'stk_analytics_consent';

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

const consentListeners = new Set();

const debugLog = (...args) => {
  if (process.env.REACT_APP_GA_DEBUG === 'true') {
    console.log('[analytics]', ...args);
  }
};

const gtag = (...args) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const isAnalyticsEnabled = () => getStoredConsent() === CONSENT_GRANTED;

export const hasConsentChoice = () => {
  const stored = getStoredConsent();
  return stored === CONSENT_GRANTED || stored === CONSENT_DENIED;
};

const notifyConsentChange = () => {
  consentListeners.forEach((listener) => listener(getStoredConsent()));
};

export const subscribeToConsent = (listener) => {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
};

const updateConsentMode = (granted) => {
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
  });
};

export const getPageTitleFromPath = (pathname) => {
  const toolSlug = getToolSlugByPath(pathname);
  if (toolSlug) {
    return getSEOData(toolSlug.seoKey).title;
  }
  if (pathname === '/about') return 'About';
  if (pathname === '/contact') return 'Contact';
  if (pathname === '/privacy') return 'Privacy Policy';
  if (pathname.startsWith('/admin')) return 'Admin';
  return 'Home';
};

export const trackPageView = (pathname, search = '') => {
  if (!isAnalyticsEnabled()) return;

  const pagePath = pathname + search;
  const pageTitle = getPageTitleFromPath(pathname);

  gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });

  debugLog('page_view', pagePath, pageTitle);
};

export const trackEvent = (eventName, params = {}) => {
  if (!isAnalyticsEnabled()) return;

  gtag('event', eventName, params);
  debugLog('event', eventName, params);
};

export const trackToolView = ({
  tool_name,
  tool_type,
  tool_category,
  page_path,
}) => {
  trackEvent('tool_view', {
    tool_name,
    tool_type,
    tool_category,
    page_path,
  });
};

export const trackCodeCopy = ({ tool_name, source }) => {
  trackEvent('code_copy', {
    tool_name,
    source,
  });
};

export const trackCodeDownload = ({ tool_name, file_name }) => {
  trackEvent('code_download', {
    tool_name,
    file_name,
  });
};

export const trackToolAction = ({ tool_name, action, detail }) => {
  trackEvent('tool_action', {
    tool_name,
    action,
    ...(detail != null ? { detail: String(detail) } : {}),
  });
};

export const trackToolGenerate = ({ tool_name, success, ...detail }) => {
  trackEvent('tool_generate', {
    tool_name,
    success: success !== false,
    ...detail,
  });
};

export const trackCopyClick = (params) => trackCodeCopy(params);

export const trackDownloadClick = (params) => trackCodeDownload(params);

export const getToolNameFromType = (toolType) => {
  const match = getCanonicalSlugForToolType(toolType);
  return match?.seoKey || toolType;
};

export const grantAnalyticsConsent = () => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, CONSENT_GRANTED);
  } catch {
    // ignore storage errors
  }
  updateConsentMode(true);
  notifyConsentChange();

  if (typeof window !== 'undefined') {
    trackPageView(window.location.pathname, window.location.search);
  }

  debugLog('consent granted');
};

export const denyAnalyticsConsent = () => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, CONSENT_DENIED);
  } catch {
    // ignore storage errors
  }
  updateConsentMode(false);
  notifyConsentChange();
  debugLog('consent denied');
};

export const initAnalytics = () => {
  if (isAnalyticsEnabled()) {
    updateConsentMode(true);
    debugLog('restored granted consent');
  }
};
